import { Component, ElementRef, Renderer2 } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';

import { environment } from '@environment';
import { SharedService } from '@services/shared.service';
import { UserService } from '@services/user.service';
import { GlobalService } from '@services/global.service';
import { FormService } from '@services/form.service';
import { PageService } from '@services/page.service';
import { FileService } from '@services/file.service';
import { TreeNode } from '@models/tree-node';
import { DataQueryOutputColumn, DataQueryOutputLinkTarget, DataQueryOutputRow, DataQueryResult } from '@models/data-query-result';
import { DataQueryDetail } from '@models/data-query-detail';
import { ColumnSortedEvent } from '../sortable-column/sort.service';
import { DataQueryResultsBaseComponent } from '../data-query-results-base/data-query-results-base.component';

@Component({
    selector: 'cp-data-query-results-grid',
    templateUrl: './data-query-results-grid.component.html',
    styleUrls: ['./data-query-results-grid.component.less'],
    standalone: false
})
export class DataQueryResultsGridComponent extends DataQueryResultsBaseComponent {
    showBusy: boolean;

    showHeader: boolean;
    showTotal: boolean;
    highlightFirst: boolean;
    highlightLast: boolean;
    bandedRows: boolean;
    bandedColumns: boolean;
    headers: any[] = [];
    rows: DataQueryOutputRow[] = [];
    footer: DataQueryOutputRow;
    id: string;
    datasetId: number;
    showNoResultsMessage: boolean;
    noResultsText: string;

    imageLocation: string = environment.ImageLocation;
    localImageLocation: string = environment.LocalImageLocation;

    languageCode: string;

    treeControl: NestedTreeControl<TreeNode> = new NestedTreeControl<TreeNode>((node) => node.Children);
    treeDataSource: MatTreeNestedDataSource<TreeNode> = new MatTreeNestedDataSource<TreeNode>();

    displayTree: boolean = false;
    get isTreeData(): boolean {
        return this.treeDataSource !== null;
    }

    fieldWidth(idx: number, node: TreeNode): string {
        if (this.headers[idx].width > 0) {
            return this.headers[idx].width + '%';
            // return this.headers[idx].width + 'px';
        }

        let otherFieldsWidth: number = 0;
        this.headers.forEach((h) => {
            otherFieldsWidth += h.width;
        });

        let level: number = 0;
        if (node) {
            level = node.Level;
        }

        const availableWidth: number = 540 - 25 - 10 * level;

        return otherFieldsWidth < availableWidth ? availableWidth - otherFieldsWidth + '%' : '0';
        // return otherFieldsWidth < availableWidth ? availableWidth - otherFieldsWidth + 'px' : '0';
    }

    fieldAlignment(idx: number): string {
        if (this.headers[idx].alignment && this.headers[idx].alignment !== '') {
            return this.headers[idx].alignment;
        }

        return 'auto';
    }

    treeNodeHasChild = (_: number, node: TreeNode) => !!node.Children && node.Children.length > 0;

    constructor(
        private globalService: GlobalService,
        private formService: FormService,
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private router: Router,
        private pageService: PageService,
        private sharedService: SharedService,
        private userService: UserService,
        private fileService: FileService,
        private sanitizer: DomSanitizer
    ) {
        super(globalService, formService, elementRef, renderer, router, pageService, sharedService, userService, fileService, sanitizer);
    }

    async processResultSet(resultSet: DataQueryResult): Promise<void> {
        this.languageCode = this._globalService.getLanguageCode();
        this.showHeader = resultSet.HeaderRow;
        this.showTotal = resultSet.TotalRow;
        this.highlightFirst = resultSet.FirstColumn;
        this.highlightLast = resultSet.LastColumn;
        this.bandedRows = resultSet.BandedRows;
        this.bandedColumns = resultSet.BandedColumns;
        this.id = resultSet.Id;
        this.datasetId = resultSet.DataSetIndex;
        this.showNoResultsMessage = resultSet.ShowNoResultsMessage;
        this.noResultsText = resultSet.NoResultsText;

        if (resultSet.QueryResults.length === 0 && !resultSet.QueryResultsTree) {
            if (resultSet.ShowHeaderWhenNoResults) {
                this.setupHeaders(resultSet.Details);
            } else {
                this.showHeader = false;
            }
        } else {
            this.setupHeaders(resultSet.Details);
        }

        if (resultSet.QueryResultsTree) {
            this.treeDataSource.data = [resultSet.QueryResultsTree];

            let count: number = 0;
            if (resultSet.QueryResults.length > 0) {
                count = resultSet.QueryResults[0].FieldValues.length;
            }
            this.setupTreeNodes(resultSet.QueryResultsTree, resultSet, count);

            this.displayTree = true;
        } else {
            this.treeDataSource = null;
        }

        let columnCount: number = 0;
        if (resultSet.QueryResults.length > 0) {
            columnCount = resultSet.QueryResults[0].FieldValues.length;
        }

        for (const data of resultSet.QueryResults) {
            const currentRow: DataQueryOutputRow = new DataQueryOutputRow();
            currentRow.columns = [];
            for (let i = 0; i < columnCount; ++i) {
                let column: DataQueryOutputColumn | null = this.formatColumn(resultSet, i, data);
                if (column) {
                    currentRow.columns.push(column);
                }
            }

            this.rows.push(currentRow);
        }

        if (this.showTotal && this.rows.length > 0) {
            this.footer = this.rows.pop();
        }
    }

    override executeDataQuery(dq: string, idx: number): void {
        if (!dq || dq === '') {
            return;
        }

        let invalidInput: boolean = false;
        const row: DataQueryOutputRow = this.rows[0];
        row.columns.forEach((col: DataQueryOutputColumn) => {
            if (col.name && col.name !== '') {
                const field = this._elementRef.nativeElement.querySelector(`#${col.name}_${idx}`);
                if (field) {
                    if (field.required && field.value === '') {
                        invalidInput = true;
                        this._renderer.setStyle(field, 'border-color', 'red');
                        this._renderer.setStyle(field, 'border-style', 'solid');
                        return;
                    }
                    this._renderer.setStyle(field, 'border-color', '');
                    this._renderer.setStyle(field, 'border-style', '');

                    dq = dq.replace('!' + col.name + '!', field.value);
                }
            }
        });

        if (!invalidInput) {
            this._formService.executeDataQuery(dq, {}).subscribe({
                next: () => this.refreshPage.emit(true)
            });
        }
    }

    private setupHeaders(details: DataQueryDetail[]): void {
        for (const detail of details) {
            if (!detail.DetailType.startsWith('Target')) {
                if (detail.DetailType.toLowerCase() !== 'treerecid' && detail.DetailType.toLowerCase() !== 'treeparentrecid') {
                    this.headers.push({
                        text: detail.Title,
                        alignment: detail.Alignment,
                        width: detail.Width,
                        sortable: detail.Sortable,
                        datatype: detail.DataType,
                        requiredField: detail.DetailType.toLowerCase() === 'iostringrequired',
                    });
                }
            }
        }
    }

    onSorted(id: string, datasetId: number, $event: ColumnSortedEvent): void {
        if (this.id === id && this.datasetId === $event.tableId && this.id === $event.queryId) {
            this.rows.sort((a: DataQueryOutputRow, b: DataQueryOutputRow) => {
                if ($event.sortDirection === 'desc') {
                    if (a.columns[$event.sortColumn].value < b.columns[$event.sortColumn].value) {
                        return -1;
                    }
                    if (a.columns[$event.sortColumn].value > b.columns[$event.sortColumn].value) {
                        return 1;
                    }
                    return 0;
                } else {
                    if (a.columns[$event.sortColumn].value > b.columns[$event.sortColumn].value) {
                        return -1;
                    }
                    if (a.columns[$event.sortColumn].value < b.columns[$event.sortColumn].value) {
                        return 1;
                    }
                    return 0;
                }
            });
        }
    }

    private setupTreeNodes(tree: TreeNode, resultSet: DataQueryResult, columnCount: number): void {
        this.processTreeNodeData(tree, resultSet, columnCount);
        tree.Children.forEach((node: TreeNode) => {
            this.setupTreeNodes(node, resultSet, columnCount);
        });
    }

    private processTreeNodeData(node: TreeNode, resultSet: DataQueryResult, columnCount: number): void {
        node['columns'] = [];
        for (let i = 0; i < columnCount; ++i) {
            const currentColumn: DataQueryOutputColumn = new DataQueryOutputColumn();
            currentColumn.imageWidth = -1;
            currentColumn.imageHeight = -1;
            switch (resultSet.Details[i].DetailType) {
                case 'IOString':
                    currentColumn.value = node.FieldValues[i];
                    currentColumn.alignment = resultSet.Details[i].Alignment;
                    currentColumn.name = resultSet.Details[i].ColumnName;
                    currentColumn.specialType = 'iostring';
                    currentColumn.width = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
                    break;
                case 'IOStringRequired':
                    currentColumn.value = node.FieldValues[i];
                    currentColumn.alignment = resultSet.Details[i].Alignment;
                    currentColumn.name = resultSet.Details[i].ColumnName;
                    currentColumn.specialType = 'iostringrequired';
                    currentColumn.width = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
                    break;
                case 'FieldMoney':
                    currentColumn.value = this._currencyFormat.format(node.FieldValues[i]);
                    currentColumn.alignment = resultSet.Details[i].Alignment;
                    currentColumn.width = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
                    break;
                case 'FieldString':
                    if (resultSet.Details[i].ReferenceSortOrder > 0) {
                        const target: DataQueryOutputLinkTarget = this.parseDestination(node.FieldValues, resultSet.Details, i);
                        currentColumn.isModal = target.isModal;
                        if (target.route.substring(0, 4) === 'http') {
                            currentColumn.destinationRoute = target.route;
                            currentColumn.linkTarget = 'anchorUrl';
                        } else if (target.route.substring(0, 11) === 'filestream/') {
                            currentColumn.destinationRoute = target.params;
                            currentColumn.destinationRouteParams = target.route.substring(target.route.indexOf('/') + 1);
                            currentColumn.specialType = target.streamType;
                            currentColumn.linkTarget = 'filestream/string';
                        } else if (target.route.substring(0, 10) === 'dataquery/') {
                            currentColumn.destinationRoute = '/';
                            currentColumn.destinationRouteParams = target.params;
                            currentColumn.linkTarget = 'dataquery/string';
                        } else {
                            currentColumn.destinationRoute = `/${target.route.toLowerCase()}`;
                            currentColumn.destinationRouteParams = target.params;
                            currentColumn.linkTarget = 'anchor';
                        }
                    }
                    currentColumn.value = node.FieldValues[i];
                    currentColumn.alignment = resultSet.Details[i].Alignment;
                    currentColumn.width = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
                    break;
                case 'FieldDate':
                    if (node.FieldValues[i] === null || node.FieldValues[i] === '') {
                        currentColumn.value = '';
                    } else {
                        const d: Date = new Date(node.FieldValues[i]);
                        if (d.getFullYear() > 1900) {
                            currentColumn.value = this._dateFormat.format(d);
                        } else {
                            currentColumn.value = '';
                        }
                    }
                    currentColumn.alignment = resultSet.Details[i].Alignment;
                    currentColumn.width = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
                    break;
                case 'FieldTime':
                    const t: Date = new Date(node.FieldValues[i]);
                    currentColumn.value = this._timeFormat.format(t);
                    currentColumn.width = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
                    break;
                case 'FieldDateTime':
                    if (node.FieldValues[i] === null || node.FieldValues[i] === '') {
                        currentColumn.value = '';
                    } else {
                        const dt: Date = new Date(node.FieldValues[i]);
                        if (dt.getFullYear() > 1900) {
                            currentColumn.value = this._dateTimeFormat.format(dt);
                        } else {
                            currentColumn.value = '';
                        }
                    }
                    currentColumn.width = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
                    break;
                case 'FieldNumber':
                    currentColumn.value = node.FieldValues[i];
                    currentColumn.alignment = resultSet.Details[i].Alignment;
                    currentColumn.width = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
                    break;
                case 'TargetFilestream':
                case 'TargetRelativeURL':
                case 'TargetURL':
                case 'TargetSQLAction':
                case 'TreeRecId':
                case 'TreeParentRecId':
                    break;
                case 'FieldImage':
                    if (resultSet.Details[i].ReferenceSortOrder > 0) {
                        const target: DataQueryOutputLinkTarget = this.parseDestination(node.FieldValues, resultSet.Details, i);
                        currentColumn.isModal = target.isModal;
                        if (target.route.substring(0, 4) === 'http') {
                            currentColumn.destinationRoute = target.route;
                            currentColumn.linkTarget = 'imageUrl';
                        } else if (target.route.substring(0, 11) === 'filestream/') {
                            currentColumn.destinationRoute = target.params;
                            currentColumn.destinationRouteParams = target.route.substring(target.route.indexOf('/') + 1);
                            currentColumn.specialType = target.streamType;
                            currentColumn.linkTarget = 'filestream/image';
                        } else if (target.route.substring(0, 10) === 'dataquery/') {
                            currentColumn.destinationRoute = '/';
                            currentColumn.destinationRouteParams = target.params;
                            currentColumn.linkTarget = 'dataquery/image';
                        } else {
                            currentColumn.destinationRoute = `/${target.route.toLowerCase()}`;
                            currentColumn.destinationRouteParams = target.params;
                            currentColumn.linkTarget = 'image';
                        }
                    }
                    currentColumn.value = node.FieldValues[i];
                    currentColumn.alignment = resultSet.Details[i].Alignment;
                    currentColumn.imageWidth = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
                    break;
                case 'FieldFilestream':
                    currentColumn.specialType = resultSet.Details[i].FilestreamType.toLowerCase();
                    currentColumn.value = node.FieldValues[i];
                    currentColumn.alignment = resultSet.Details[i].Alignment;
                    currentColumn.imageWidth = resultSet.Details[i].ImageWidth;
                    currentColumn.imageHeight = resultSet.Details[i].ImageHeight;
                    node['columns'].push(currentColumn);
                    break;
                case 'FieldIframe':
                    if (resultSet.Details[i].ReferenceSortOrder > 0) {
                        const target: DataQueryOutputLinkTarget = this.parseDestination(node.FieldValues, resultSet.Details, i);
                        currentColumn.isModal = target.isModal;
                        if (target.route.substring(0, 4) === 'http') {
                            currentColumn.destinationRoute = target.route;
                            currentColumn.linkTarget = 'imageUrl';
                        } else if (target.route.substring(0, 11) === 'filestream/') {
                            currentColumn.destinationRoute = target.params;
                            currentColumn.destinationRouteParams = target.route.substring(target.route.indexOf('/') + 1);
                            currentColumn.specialType = target.streamType;
                            currentColumn.linkTarget = 'filestream/iframe';
                        } else if (target.route.substring(0, 10) === 'dataquery/') {
                            currentColumn.destinationRoute = '/';
                            currentColumn.destinationRouteParams = target.params;
                            currentColumn.linkTarget = 'dataquery/image';
                        } else {
                            currentColumn.destinationRoute = `/${target.route.toLowerCase()}`;
                            currentColumn.destinationRouteParams = target.params;
                            currentColumn.linkTarget = 'image';
                        }
                    }
                    currentColumn.value = node.FieldValues[i];
                    currentColumn.alignment = resultSet.Details[i].Alignment;
                    currentColumn.imageWidth = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
                    break;
                default:
                    currentColumn.value = node.FieldValues[i];
                    currentColumn.alignment = resultSet.Details[i].Alignment;
                    currentColumn.width = resultSet.Details[i].Width;
                    node['columns'].push(currentColumn);
            }
        }
    }
}
